/* Copyright (c) 2020-present CloudIO Inc */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  List,
  ListItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Stack,
  LinearProgress,
  IconButton,
  CircularProgress,
  Divider,
  useTheme,
  ListItemText,
} from '@mui/material';
import Icon from 'icons/Icon';
interface CodeIssue {
  line: number;
  severity: 'Error' | 'Warning';
  category: string;
  message: string;
  remediation: string;
  highlight: string;
}

interface PracticeHighlight {
  line: number;
  message: string;
  highlight: string;
}

interface LoopInsight {
  line: number;
  kind: string;
  depth: number;
  contains_db: boolean;
  contains_await: boolean;
  // estimated_cost: number;
}

interface CoverageHit {
  label: string;
  occurrences: number;
}

interface CoverageSummary {
  matched_patterns: number;
  total_patterns: number;
  percent: number;
  hits: CoverageHit[];
}

interface SectionScore {
  name: string;
  score: number;
}

interface AstSummary {
  total_functions: number;
  avg_function_len: number;
  max_loop_depth: number;
  api_calls_inside_loops: number;
}

export interface EvaluationResult {
  issues: CodeIssue[];
  practices: PracticeHighlight[];
  score: number;
  verdict: 'Approve' | 'Reject';
  analyzed_lines: number;
  loop_insights: LoopInsight[];
  coverage: CoverageSummary;
  section_scores: SectionScore[];
  ast_summary?: AstSummary;
}

const Report: React.FC<{
  result: EvaluationResult | null;
  loading?: boolean;
}> = ({ result, loading = false }) => {
  const theme = useTheme();
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          Analyzing code...
        </Typography>
      </Container>
    );
  }

  if (!result) return null;

  const isApproved = result.verdict === 'Approve';
  const statusColor = isApproved ? 'success.main' : 'error.main';
  const errors = result.issues.filter((i) => i.severity === 'Error');
  const warnings = result.issues.filter((i) => i.severity === 'Warning');

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        py: 3,
        pb: 8,
        pr: 3,
        pl: 3,
        pt: 2,
        height: 'auto',
        overflow: 'visible',
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 3,
          borderLeft: 6,
          borderColor: statusColor,
          boxShadow: 3,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5} minWidth={140}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: isApproved ? 'success.light' : 'error.light',
              color: isApproved ? 'success.dark' : 'error.dark',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 3,
            }}
          >
            <Icon iconName={isApproved ? 'check' : 'times'} />
          </Box>
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              lineHeight={1}
            >
              VERDICT
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
              {result.verdict.toUpperCase()}
            </Typography>
          </Box>
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ display: { xs: 'none', md: 'block' } }}
        />

        <Box sx={{ flexGrow: 1, minWidth: 200 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="baseline"
            mb={0.5}
          >
            <Typography
              variant="body2"
              fontWeight="medium"
              color="text.secondary"
            >
              Quality Score
            </Typography>
            <Typography variant="h6" fontWeight="800" color={statusColor}>
              {result.score}
              <span
                style={{ fontSize: '0.8em', color: '#999', fontWeight: 400 }}
              >
                /100
              </span>
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={result.score}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'grey.100',
              '& .MuiLinearProgress-bar': { bgcolor: statusColor },
              boxShadow: 2,
            }}
          />
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ display: { xs: 'none', md: 'block' } }}
        />

        <Box display="flex" gap={4} sx={{ textAlign: 'center' }}>
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              LINES
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold">
              {result.analyzed_lines.toLocaleString()}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              ISSUES
            </Typography>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color={result.issues.length > 0 ? 'error.main' : 'text.primary'}
            >
              {result.issues.length}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Box
        sx={{
          columnCount: { xs: 1, lg: 2 }, // 2 Columns on large screens
          columnGap: 3, // Space between columns
          '& > *': {
            // Apply to all direct children
            marginBottom: 3, // Space bottom
            breakInside: 'avoid', // Don't chop a card in half
            display: 'inline-block', // Fixes some rendering glitches in columns
            width: '100%', // Ensure full width of the column
          },
        }}
      >
        <Paper sx={{ p: 0, boxShadow: 3, borderRadius: 2 }}>
          <Box sx={{ p: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6" fontWeight="bold">
                Critical Issues
              </Typography>
              <Chip
                size="small"
                label={`${errors.length} Errors, ${warnings.length} Warnings`}
              />
            </Stack>
          </Box>
          <Box
            sx={{
              p: 3,
              pt: 1,
              maxHeight: 500,
              overflowY: 'auto',
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#e0e0e0',
                borderRadius: '4px',
              },
            }}
          >
            {result.issues.length === 0 ? (
              <Alert
                severity="success"
                icon={<Icon iconName={'check-circle'} />}
                sx={{ mt: 2 }}
              >
                No issues detected! 🎉
              </Alert>
            ) : (
              <List disablePadding>
                {result.issues.map((issue, idx) => (
                  <ListItem
                    key={idx}
                    divider
                    alignItems="flex-start"
                    sx={{
                      px: 0,
                      py: 3,
                      borderLeft: '4px solid',
                      borderLeftColor:
                        issue.severity === 'Error'
                          ? 'error.main'
                          : 'warning.main',
                      pl: 2,
                      pr: 2,
                      my: 1,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {issue.category}
                          </Typography>
                          <Chip
                            label={`Line ${issue.line}`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20 }}
                          />
                        </Stack>
                        <Chip
                          label={issue.severity}
                          color={
                            issue.severity === 'Error' ? 'error' : 'warning'
                          }
                          size="small"
                          sx={{ height: 20 }}
                        />
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1.5, fontWeight: 400 }}
                      >
                        {issue.message}
                      </Typography>
                      {issue.highlight && (
                        <Paper
                          sx={{
                            p: 1.5,
                            bgcolor: '#0b1421',
                            color: '#e6eef8',
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            borderRadius: 1,
                            position: 'relative',
                          }}
                        >
                          {issue.highlight}
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              color: 'grey.400',
                            }}
                            onClick={() => copyToClipboard(issue.highlight)}
                          >
                            <Icon iconName={'copy'} />
                          </IconButton>
                        </Paper>
                      )}
                      {issue.remediation && (
                        <Alert
                          severity="success"
                          sx={{
                            mt: 1.5,
                            py: 0,
                            px: 1,
                            '& .MuiAlert-icon': { py: 0.5 },
                          }}
                        >
                          <Typography variant="caption" fontWeight="bold">
                            Fix:{' '}
                          </Typography>
                          <Typography variant="caption">
                            {issue.remediation}
                          </Typography>
                        </Alert>
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Paper>
        {result.loop_insights.length > 0 && (
          <Paper sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              fontWeight="bold"
              sx={{ px: 1 }}
            >
              Loop Risk Analysis
            </Typography>
            <TableContainer sx={{ maxHeight: 350 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ bgcolor: 'background.paper', textAlign: 'center' }}
                    >
                      Line
                    </TableCell>
                    <TableCell
                      sx={{ bgcolor: 'background.paper', textAlign: 'center' }}
                    >
                      Type
                    </TableCell>
                    <TableCell
                      sx={{ bgcolor: 'background.paper', textAlign: 'center' }}
                    >
                      Scope Depth
                    </TableCell>
                    <TableCell
                      sx={{ bgcolor: 'background.paper', textAlign: 'center' }}
                    >
                      DB
                    </TableCell>
                    <TableCell
                      sx={{ bgcolor: 'background.paper', textAlign: 'center' }}
                    >
                      Async
                    </TableCell>
                    {/* <TableCell
                      align="right"
                      sx={{ bgcolor: 'background.paper' }}
                    >
                      Cost
                    </TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.loop_insights.map((insight, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <strong>{insight.line}</strong>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {insight.kind}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          label={insight.depth}
                          size="small"
                          color={insight.depth > 2 ? 'warning' : 'default'}
                          variant={insight.depth > 2 ? 'filled' : 'outlined'}
                          sx={{ height: 20, minWidth: 20 }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {insight.contains_db ? (
                          <Icon
                            iconName="circle-exclamation"
                            color="warning"
                            iconType="solid"
                            height={12}
                            width={12}
                            data-tip={'Updated'}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {insight.contains_await ? (
                          <Icon
                            iconName="circle-exclamation"
                            color="error"
                            iconType="solid"
                            height={12}
                            width={12}
                            data-tip={'Updated'}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      {/* <TableCell
                        align="right"
                        sx={{
                          fontWeight: 'bold',
                          color:
                            insight.estimated_cost > 100
                              ? 'error.main'
                              : 'text.primary',
                        }}
                      >
                        {insight.estimated_cost}
                      </TableCell> */}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
        <Paper sx={{ p: 3, boxShadow: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            CloudIO Pattern Coverage
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
            <Typography
              variant="h4"
              sx={{ color: 'success.main', fontWeight: 'bold', mr: 1 }}
            >
              {result.coverage.percent.toFixed(0)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              covered
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={result.coverage.percent}
            sx={{ height: 6, borderRadius: 3, mb: 2, bgcolor: 'grey.100' }}
            color="success"
          />
          {result.coverage.hits.filter((h) => h.occurrences > 0).length > 0 && (
            <Box mt={2}>
              <Typography
                variant="caption"
                fontWeight="bold"
                color="text.secondary"
              >
                TOP PATTERNS
              </Typography>
              <List dense disablePadding>
                {result.coverage.hits
                  .filter((h) => h.occurrences > 0)
                  .slice(0, 3)
                  .map((hit, i) => (
                    <ListItem
                      key={i}
                      disablePadding
                      sx={{ py: 0.5, justifyContent: 'space-between' }}
                    >
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ maxWidth: '80%', fontWeight: '600' }}
                      >
                        {hit.label}
                      </Typography>
                      <Typography variant="caption" fontWeight="bold">
                        {hit.occurrences}x
                      </Typography>
                    </ListItem>
                  ))}
              </List>
            </Box>
          )}
        </Paper>
        {result.section_scores.length > 0 && (
          <Paper sx={{ p: 3, boxShadow: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Section Scores
            </Typography>
            <Stack spacing={2} mt={1}>
              {result.section_scores.map((section, idx) => (
                <Box key={idx}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" fontWeight="medium">
                      {section.name}
                    </Typography>
                    <Typography variant="caption">
                      {section.score}/100
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={section.score}
                    sx={{ height: 6, borderRadius: 3, bgcolor: 'grey.100' }}
                  />
                </Box>
              ))}
            </Stack>
          </Paper>
        )}
        {result.ast_summary && (
          <Paper sx={{ p: 3, boxShadow: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              AST Metrics
            </Typography>
            <Stack spacing={1.5}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Functions
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {result.ast_summary.total_functions}
                </Typography>
              </Box>
              <Divider />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Average Function Length
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {result.ast_summary.avg_function_len}
                </Typography>
              </Box>
              <Divider />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Max Depth
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color={
                    result.ast_summary.max_loop_depth > 3
                      ? 'warning.main'
                      : 'inherit'
                  }
                >
                  {result.ast_summary.max_loop_depth}
                </Typography>
              </Box>
              <Divider />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Loop APIs
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color={
                    result.ast_summary.api_calls_inside_loops > 0
                      ? 'error.main'
                      : 'inherit'
                  }
                >
                  {result.ast_summary.api_calls_inside_loops}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}
        <Paper sx={{ p: 3, boxShadow: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Good Practices ({result.practices.length})
          </Typography>
          <List dense disablePadding>
            {result.practices.slice(0, 3).map((practice, idx) => (
              <ListItem key={idx} divider>
                <Icon
                  iconName={'check-circle'}
                  style={{ marginRight: 8, color: 'green' }}
                />
                <ListItemText
                  primary={practice.message}
                  secondary={practice.line !== 0 && `Line ${practice.line}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Box>
    </Container>
  );
};

export default Report;
